
import { Player, BadgeType, Match, TournamentMatch } from '../types';
import { ELO_K_FACTOR_PROVISIONAL, ELO_K_FACTOR_ESTABLISHED, ELO_K_FACTOR_MAJOR_MULTIPLIER, ELO_K_FACTOR_MINOR_MULTIPLIER, ELO_K_FACTOR_OLD_MATCH_MULTIPLIER, BADGES, INITIAL_ELO } from '../constants';
import { dataManager } from './dataManager';
import { MANUAL_BADGES } from '../data/seedData';
import { calculatePlayerStats } from './matchAnalytics';

// Cache for recalculation optimization
let lastMatchesHash = "";

/**
 * HELPER: Calculate ELO Expectation
 * probability of A beating B
 */
export const getExpectedScore = (ratingA: number, ratingB: number): number => {
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 40));
};

/**
 * HELPER: Safe Badge Increment
 * Now exported for use in tournamentUtils
 */
export const incrementBadge = (player: Player, badge: BadgeType) => {
  const def = BADGES[badge];
  if (!def) return;
  if (def.frequency === 'Once') {
    player.badges[badge] = 1;
  } else {
    player.badges[badge] = (player.badges[badge] || 0) + 1;
  }
};

/**
 * THE LAB: ELO SIMULATION
 * Pure function for predictive analysis in the UI.
 */
export const simulateEloExchange = (
  p1: Player, 
  p2: Player, 
  context: { isMajor: boolean, isFinal: boolean } = { isMajor: false, isFinal: false }
) => {
  const expP1_W = getExpectedScore(p1.elo, p2.elo);
  const expP2_L = getExpectedScore(p2.elo, p1.elo);

  let kP1_W = p1.stats.matchesPlayed <= 20 ? ELO_K_FACTOR_PROVISIONAL : ELO_K_FACTOR_ESTABLISHED;
  let kP2_L = p2.stats.matchesPlayed <= 20 ? ELO_K_FACTOR_PROVISIONAL : ELO_K_FACTOR_ESTABLISHED;

  if (p1.advancedStats.currentStreak >= 3) { kP1_W *= 1.15; kP2_L *= 1.15; }
  if (context.isFinal) {
     const mult = context.isMajor ? ELO_K_FACTOR_MAJOR_MULTIPLIER : ELO_K_FACTOR_MINOR_MULTIPLIER;
     kP1_W *= mult;
     kP2_L *= mult;
  }

  const p1Delta_Win = kP1_W * (1 - expP1_W);
  const p2Delta_Loss = kP2_L * (0 - expP2_L);

  const expP2_W = getExpectedScore(p2.elo, p1.elo);
  const expP1_L = getExpectedScore(p1.elo, p2.elo);

  let kP2_W = p2.stats.matchesPlayed <= 20 ? ELO_K_FACTOR_PROVISIONAL : ELO_K_FACTOR_ESTABLISHED;
  let kP1_L = p1.stats.matchesPlayed <= 20 ? ELO_K_FACTOR_PROVISIONAL : ELO_K_FACTOR_ESTABLISHED;

  if (p2.advancedStats.currentStreak >= 3) { kP2_W *= 1.15; kP1_L *= 1.15; }
  if (context.isFinal) {
     const mult = context.isMajor ? ELO_K_FACTOR_MAJOR_MULTIPLIER : ELO_K_FACTOR_MINOR_MULTIPLIER;
     kP2_W *= mult;
     kP1_L *= mult;
  }

  const p2Delta_Win = kP2_W * (1 - expP2_W);
  const p1Delta_Loss = kP1_L * (0 - expP1_L);

  return {
    p1WinProb: expP1_W,
    p2WinProb: expP2_W,
    ifP1Wins: { p1Delta: p1Delta_Win, p2Delta: p2Delta_Loss },
    ifP2Wins: { p1Delta: p1Delta_Loss, p2Delta: p2Delta_Win }
  };
};

/**
 * Calculates the win probability of player 1 in a given match state.
 * Uses a simplified Z-score model incorporating score difference, ELO difference, and points remaining.
 */
export const calculateWinProbability = (s1: number, s2: number, e1: number, e2: number, winLimit: number): number => {
  const d = s1 - s2;
  const max = Math.max(s1, s2);
  const isDeuce = max >= (winLimit - 1);
  if (s1 >= winLimit && d >= 2) return 1.0;
  if (s2 >= winLimit && -d >= 2) return 0.0;
  
  // Adjust remaining points estimate based on deuce
  const rem = isDeuce ? 2 : Math.max(2, winLimit - max);
  
  // Z-Score calc with ELO bias
  const z = (d + (e1 - e2) / 60) / (Math.sqrt(rem) * 1.1);
  return Math.min(0.99, Math.max(0.01, 1 / (1 + Math.exp(-z))));
};

// Helper to merge calculated deep stats into player record
const mergeDeepStats = (player: Player, stats: any) => {
    player.advancedStats.aces += stats.aces;
    player.advancedStats.winners += stats.winners;
    player.advancedStats.errors += stats.errors;
    player.advancedStats.servicePointsWon += stats.spw;
    player.advancedStats.servicePointsTotal += stats.spt;
    player.advancedStats.returnPointsWon += stats.rpw;
    player.advancedStats.returnPointsTotal += stats.rpt;
    player.advancedStats.gamePointsConverted += stats.gpc;
    player.advancedStats.gamePointOpportunities += stats.gpo;
    player.advancedStats.totalPointsPlayed += stats.totalPoints;
};

// --- Shared helpers for updateStatsForMatch & recalculateStats ---

const getKFactors = (
  winner: Player, loser: Player,
  ctx: { isFinal: boolean; isMajor: boolean; isOld?: boolean }
): { kWinner: number; kLoser: number } => {
  let kWinner = winner.stats.matchesPlayed <= 20 ? ELO_K_FACTOR_PROVISIONAL : ELO_K_FACTOR_ESTABLISHED;
  let kLoser = loser.stats.matchesPlayed <= 20 ? ELO_K_FACTOR_PROVISIONAL : ELO_K_FACTOR_ESTABLISHED;
  if (winner.advancedStats.currentStreak >= 3) { kWinner *= 1.15; kLoser *= 1.15; }
  if (ctx.isFinal) {
    const finalMult = ctx.isMajor ? ELO_K_FACTOR_MAJOR_MULTIPLIER : ELO_K_FACTOR_MINOR_MULTIPLIER;
    kWinner *= finalMult;
    kLoser *= finalMult;
  }
  if (ctx.isOld) {
    kWinner *= ELO_K_FACTOR_OLD_MATCH_MULTIPLIER;
    kLoser *= ELO_K_FACTOR_OLD_MATCH_MULTIPLIER;
  }
  return { kWinner, kLoser };
};

const updateStreaksAndPeak = (winner: Player, loser: Player) => {
  if (winner.elo > (winner.advancedStats.peakElo || 0)) winner.advancedStats.peakElo = winner.elo;
  winner.advancedStats.currentStreak++;
  if (winner.advancedStats.currentStreak > winner.advancedStats.longestStreak) {
    winner.advancedStats.longestStreak = winner.advancedStats.currentStreak;
  }
  loser.advancedStats.currentStreak = 0;
};

const checkMatchBadges = (winner: Player, winScore: number, loseScore: number, history?: any[], winnerIsP1?: boolean) => {
  if (loseScore === 0) incrementBadge(winner, BadgeType.DONUT);
  if (loseScore < 5) incrementBadge(winner, BadgeType.THE_WALL);
  if (winScore >= 27) incrementBadge(winner, BadgeType.MARATHON_MAN);
  if (winScore === 21 && loseScore === 19) incrementBadge(winner, BadgeType.CALCULATED);
  // Lazarus: Win after trailing by 5+ points
  if (history && history.length > 0 && winnerIsP1 !== undefined) {
    let maxDeficit = 0;
    for (const pt of history) {
      const deficit = winnerIsP1 ? (pt.score2 - pt.score1) : (pt.score1 - pt.score2);
      if (deficit > maxDeficit) maxDeficit = deficit;
    }
    if (maxDeficit >= 5) incrementBadge(winner, BadgeType.THE_LAZARUS);
  }
};

const checkClutchMatch = (winner: Player, loser: Player, winScore: number, loseScore: number) => {
  const isClutch = Math.abs(winScore - loseScore) <= 2 || (winScore >= 20 && loseScore >= 20);
  if (isClutch) {
    winner.advancedStats.clutchWins++;
    winner.advancedStats.clutchTotal++;
    loser.advancedStats.clutchTotal++;
  }
};

/**
 * INCREMENTAL UPDATE (O(1))
 * Used for live scoring updates. Returns ELO changes for immediate UI feedback.
 */
export const updateStatsForMatch = (
  matchData: { 
    p1: string; 
    p2: string; 
    s1: number; 
    s2: number; 
    winner: string | null; 
    isWalkover?: boolean;
    history?: any[]; // Passed for Lazarus & Deep Stat analysis
  },
  context: { isFinal: boolean; isMajor: boolean } = { isFinal: false, isMajor: false }
): { p1: number, p2: number, p1New: number, p2New: number, updatedPlayers?: Player[] } => {
  const p1 = dataManager.getPlayer(matchData.p1);
  const p2 = dataManager.getPlayer(matchData.p2);

  if (!p1 || !p2) return { p1: 0, p2: 0, p1New: 0, p2New: 0 };

  const isGuestMatch = p1.isGuest || p2.isGuest;
  const isWalkover = !!matchData.isWalkover;
  
  let winner: Player;
  let loser: Player;
  let winScore: number;
  let loseScore: number;

  if (matchData.winner === p1.id) {
    winner = p1; loser = p2;
    winScore = matchData.s1; loseScore = matchData.s2;
  } else {
    winner = p2; loser = p1;
    winScore = matchData.s2; loseScore = matchData.s1;
  }

  winner.stats.wins += 1;
  winner.stats.matchesPlayed += 1;
  loser.stats.losses += 1;
  loser.stats.matchesPlayed += 1;

  winner.advancedStats.form.push('W');
  if (winner.advancedStats.form.length > 5) winner.advancedStats.form.shift();
  loser.advancedStats.form.push('L');
  if (loser.advancedStats.form.length > 5) loser.advancedStats.form.shift();

  if (isGuestMatch || isWalkover) {
     return { p1: 0, p2: 0, p1New: p1.elo, p2New: p2.elo, updatedPlayers: [winner, loser] };
  }

  winner.stats.pointsWon += winScore;
  winner.stats.pointsLost += loseScore;
  loser.stats.pointsWon += loseScore;
  loser.stats.pointsLost += winScore;

  const expWinner = getExpectedScore(winner.elo, loser.elo);
  const expLoser = getExpectedScore(loser.elo, winner.elo);

  const { kWinner, kLoser } = getKFactors(winner, loser, { isFinal: context.isFinal, isMajor: context.isMajor });

  const oldWinnerElo = winner.elo;
  const oldLoserElo = loser.elo;

  winner.elo = Math.min(100.0, Math.max(0.0, winner.elo + kWinner * (1 - expWinner)));
  loser.elo = Math.min(100.0, Math.max(0.0, loser.elo + kLoser * (0 - expLoser)));

  updateStreaksAndPeak(winner, loser);

  // --- Deep Stat Analysis & Badge Checks ---
  const isTeamGame = winner.id.startsWith('team_');
  const CLEAN_SHEET_THRESHOLD = isTeamGame ? 5 : 10;

  const winnerIsP1 = matchData.winner === matchData.p1;
  checkMatchBadges(winner, winScore, loseScore, matchData.history, winnerIsP1);
  if (winner.stats.matchesPlayed === 100) incrementBadge(winner, BadgeType.IRON_MAN);

  // Deep Stats Update
  if (!isWalkover) {
      if (loseScore < CLEAN_SHEET_THRESHOLD) winner.advancedStats.cleanSheets++;

      // Update advanced stats from point history if available
      if (matchData.history && matchData.history.length > 0) {
          const winnerStats = calculatePlayerStats([{...matchData, winner: winner.id, s1: winScore, s2: loseScore, p1: winner.id, p2: loser.id}], winner.id);
          const loserStats = calculatePlayerStats([{...matchData, winner: winner.id, s1: winScore, s2: loseScore, p1: winner.id, p2: loser.id}], loser.id);
          mergeDeepStats(winner, winnerStats);
          mergeDeepStats(loser, loserStats);
      }
  }

  checkClutchMatch(winner, loser, winScore, loseScore);

  return {
    p1: matchData.winner === matchData.p1 ? (winner.elo - oldWinnerElo) : (loser.elo - oldLoserElo),
    p2: matchData.winner === matchData.p2 ? (winner.elo - oldWinnerElo) : (loser.elo - oldLoserElo),
    p1New: matchData.winner === matchData.p1 ? winner.elo : loser.elo,
    p2New: matchData.winner === matchData.p2 ? winner.elo : loser.elo,
    updatedPlayers: [winner, loser]
  };
};

/**
 * Helper to reset a player's stats to baseline before recalculation.
 */
const initializePlayerStats = (p: Player, isDoubles: boolean): Player => {
  const initialBadges = (!isDoubles && MANUAL_BADGES[p.id]) ? { ...MANUAL_BADGES[p.id] } : {};
  return {
    ...p,
    elo: INITIAL_ELO,
    stats: { wins: 0, losses: 0, championships: 0, pointsWon: 0, pointsLost: 0, matchesPlayed: 0, majorTitles: 0, minorTitles: 0 },
    advancedStats: { 
        clutchWins: 0, clutchTotal: 0, nemesisId: null, nemesisWinPct: 0, longestStreak: 0, currentStreak: 0, peakElo: INITIAL_ELO, form: [],
        servicePointsWon: 0, servicePointsTotal: 0, returnPointsWon: 0, returnPointsTotal: 0,
        aces: 0, winners: 0, errors: 0, totalPointsPlayed: 0,
        deuceWins: 0, deuceTotal: 0, gamePointsConverted: 0, gamePointOpportunities: 0,
        cleanSheets: 0
    },
    badges: initialBadges, 
  };
};

/**
 * FULL RECALCULATION (O(N_points + N_matches))
 * Rebuilds the entire roster's statistics from ground truth match history.
 * Optimized with a single pass over matches and points.
 * MEMOIZED: Skips recalculation if matches + tournaments haven't changed.
 */
export const recalculateStats = (force = false): Player[] => {
  const allMatches = dataManager.getAllMatches().sort((a,b) => (a.timestamp || 0) - (b.timestamp || 0));
  const tournaments = dataManager.getTournaments();
  
  // Optimization: Hash check to avoid expensive recalc — includes match content for correctness
  const matchSummary = allMatches.map(m => `${m.id}:${m.s1}-${m.s2}:${m.winner || ''}:${m.isWalkover ? 'wo' : ''}`).join('|');
  const tournamentSummary = tournaments.map(t => `${t.id}:${t.status}:${t.winnerId || ''}`).join('|');
  const currentHash = `${matchSummary}_${tournamentSummary}`;
  if (!force && currentHash === lastMatchesHash) {
      return dataManager.getPlayers();
  }
  lastMatchesHash = currentHash;

  const playersMap = new Map<string, Player>();
  const rawPlayers = dataManager.getPlayers();
  const isDoubles = dataManager.getGameMode() === 'doubles';
  const WIN_LIMIT = isDoubles ? 11 : 21;
  const CLEAN_SHEET_THRESHOLD = isDoubles ? 5 : 10;
  
  // 1. Initialize clean slate for all players
  rawPlayers.forEach(p => {
    playersMap.set(p.id, initializePlayerStats(p, isDoubles));
  });

  const h2hStreakMap = new Map<string, Map<string, number>>();
  const deuceStreakMap = new Map<string, number>();
  const h2hMap = new Map<string, Map<string, { wins: number, total: number }>>();

  const now = Date.now();
  const ONE_YEAR_MS = 31536000000;
  const updates: any[] = [];
  
  // 2. Process all matches in chronological order
  allMatches.forEach(match => {
    const p1 = playersMap.get(match.p1);
    const p2 = playersMap.get(match.p2);
    if (!p1 || !p2) return;

    const isWalkover = !!match.isWalkover;
    const isGuestMatch = !!match.isGuest || p1.isGuest || p2.isGuest;
    const isFinal = match.round === 'Final' || match.round === 'Championship' || (!match.nextMatchId && !!match.tournamentId);
    const isMajor = !!match.isMajor;
    const isOld = (now - (match.timestamp || 0)) > ONE_YEAR_MS;

    let winnerId: string;
    let loserId: string;
    const s1 = match.s1;
    const s2 = match.s2;

    if (isWalkover) {
        winnerId = match.winner === match.p1 ? match.p1 : match.p2;
    } else {
        if (match.winner) {
            winnerId = match.winner;
        } else {
            if (s1 > s2) winnerId = match.p1;
            else if (s2 > s1) winnerId = match.p2;
            else return; // Tie or incomplete
        }
    }
    loserId = winnerId === match.p1 ? match.p2 : match.p1;

    const winner = playersMap.get(winnerId)!;
    const loser = playersMap.get(loserId)!;

    // Standard Tallies
    winner.stats.wins += 1;
    winner.stats.matchesPlayed += 1;
    loser.stats.losses += 1;
    loser.stats.matchesPlayed += 1;
    
    winner.advancedStats.form.push('W');
    loser.advancedStats.form.push('L');

    // Update H2H for Nemesis tracking
    [ [winnerId, loserId, true], [loserId, winnerId, false] ].forEach(([me, opp, isWin]) => {
        if (!h2hMap.has(me as string)) h2hMap.set(me as string, new Map());
        const myMap = h2hMap.get(me as string)!;
        const stats = myMap.get(opp as string) || { wins: 0, total: 0 };
        if (isWin) stats.wins++;
        stats.total++;
        myMap.set(opp as string, stats);
    });

    const deuceThreshold = WIN_LIMIT - 1;
    const isDeuceGame = s1 >= deuceThreshold && s2 >= deuceThreshold;

    if (!isGuestMatch) {
       // Rent Free & Ice Veins Logic
       if (!h2hStreakMap.has(winnerId)) h2hStreakMap.set(winnerId, new Map());
       if (!h2hStreakMap.has(loserId)) h2hStreakMap.set(loserId, new Map());
       const wStreaks = h2hStreakMap.get(winnerId)!;
       const lStreaks = h2hStreakMap.get(loserId)!;
       const currentW = (wStreaks.get(loserId) || 0) + 1;
       wStreaks.set(loserId, currentW);
       lStreaks.set(winnerId, 0); 
       if (currentW === 5) incrementBadge(winner, BadgeType.RENT_FREE);

       if (isDeuceGame) {
          const wIce = (deuceStreakMap.get(winnerId) || 0) + 1;
          deuceStreakMap.set(winnerId, wIce);
          deuceStreakMap.set(loserId, 0); 
          if (wIce === 3) incrementBadge(winner, BadgeType.ICE_VEINS);
       } else {
          deuceStreakMap.set(winnerId, 0);
          deuceStreakMap.set(loserId, 0);
       }
    }

    if (isGuestMatch) return; 

    // --- DEEP POINT-LEVEL ANALYSIS ---
    const loserScore = winnerId === match.p1 ? s2 : s1;
    const winnerScore = winnerId === match.p1 ? s1 : s2;

    if (!isWalkover) {
        if (loserScore < CLEAN_SHEET_THRESHOLD) winner.advancedStats.cleanSheets++;

        // Deuce Stats
        if (isDeuceGame) {
            winner.advancedStats.deuceWins++;
            winner.advancedStats.deuceTotal++;
            loser.advancedStats.deuceTotal++;
        }

        // Aggregate Deep Stats from History (Spider Graph Data)
        if (match.history && match.history.length > 0) {
            const winnerStats = calculatePlayerStats([{...match, winner: winnerId}], winnerId);
            const loserStats = calculatePlayerStats([{...match, winner: winnerId}], loserId);
            mergeDeepStats(winner, winnerStats);
            mergeDeepStats(loser, loserStats);
        }

        // ELO LOGIC
        winner.stats.pointsWon += winnerScore;
        winner.stats.pointsLost += loserScore;
        loser.stats.pointsWon += loserScore;
        loser.stats.pointsLost += winnerScore;

        const expWinner = getExpectedScore(winner.elo, loser.elo);
        const expLoser = getExpectedScore(loser.elo, winner.elo);

        const { kWinner, kLoser } = getKFactors(winner, loser, { isFinal, isMajor, isOld });

        const newWinnerElo = Math.min(100.0, Math.max(0.0, winner.elo + kWinner * (1 - expWinner)));
        const newLoserElo = Math.min(100.0, Math.max(0.0, loser.elo + kLoser * (0 - expLoser)));

        winner.elo = newWinnerElo;
        loser.elo = newLoserElo;

        // SNAPSHOT MIGRATION / BACKFILL
        const p1EloAfter = winnerId === match.p1 ? newWinnerElo : newLoserElo;
        const p2EloAfter = winnerId === match.p2 ? newWinnerElo : newLoserElo;

        if (match.p1EloAfter === undefined || match.p2EloAfter === undefined || Math.abs(match.p1EloAfter - p1EloAfter) > 0.01) {
            updates.push({
                matchId: match.id,
                tournamentId: match.tournamentId,
                p1Elo: p1EloAfter,
                p2Elo: p2EloAfter
            });
        }

        updateStreaksAndPeak(winner, loser);
        const recalcWinnerIsP1 = winnerId === match.p1;
        checkMatchBadges(winner, winnerScore, loserScore, match.history, recalcWinnerIsP1);
        checkClutchMatch(winner, loser, winnerScore, loserScore);
    }
  });

  // 3. Apply Batch Updates for Snapshots
  if (updates.length > 0) {
      console.log(`KFPPC: Backfilling ELO snapshots for ${updates.length} matches.`);
      dataManager.batchUpdateMatchSnapshots(updates);
  }

  // 3. Process Tournaments for Title Counts
  tournaments.forEach(t => {
      if (t.winnerId && playersMap.has(t.winnerId)) {
          const p = playersMap.get(t.winnerId)!;
          p.stats.championships++;
          if (t.isMajor) p.stats.majorTitles++; else p.stats.minorTitles++;
      }
  });

  // 4. Final Polish: Nemesis, Form Trim, Iron Man
  playersMap.forEach((p, pid) => {
     if (p.stats.matchesPlayed >= 100) incrementBadge(p, BadgeType.IRON_MAN);
     p.advancedStats.form = p.advancedStats.form.slice(-5);
     if (!p.advancedStats.peakElo) p.advancedStats.peakElo = p.elo;

     const myH2H = h2hMap.get(pid);
     if (myH2H) {
         let worstRate = 1.1;
         let nemesisId = null;
         myH2H.forEach((stats, oppId) => {
             const rate = stats.wins / stats.total;
             if (stats.total >= 3 && rate < worstRate) {
                 worstRate = rate;
                 nemesisId = oppId;
             }
         });
         if (nemesisId) {
             p.advancedStats.nemesisId = nemesisId;
             p.advancedStats.nemesisWinPct = worstRate;
         }
     }
  });

  const sortedPlayers = Array.from(playersMap.values()).sort((a, b) => b.elo - a.elo);
  dataManager.setPlayers(sortedPlayers);
  return sortedPlayers;
};

/**
 * GET STANDINGS
 */
export const getCalculatedStandings = (): Player[] => {
  return dataManager.getPlayers();
};

/**
 * HEAD TO HEAD COMPARISON
 */
export const getHeadToHead = (idA: string, idB: string) => {
  const allMatches = dataManager.getAllMatches();
  let winsA = 0;
  let winsB = 0;

  allMatches.forEach(m => {
    if ((m.p1 === idA && m.p2 === idB) || (m.p1 === idB && m.p2 === idA)) {
       let winnerId = m.winner;
       if (!winnerId) {
          if (m.s1 > m.s2) winnerId = m.p1;
          else if (m.s2 > m.s1) winnerId = m.p2;
       }
       if (winnerId) {
          if (winnerId === idA) winsA++;
          else if (winnerId === idB) winsB++;
       }
    }
  });

  return { winsA, winsB, total: winsA + winsB };
};

/**
 * CALCULATE PLAYER ARCHETYPE and RADAR DATA
 * This function computes derived statistics for a player to determine their playstyle archetype
 * and generates the necessary data for the radar chart visualization.
 */
export const calculatePlayerArchetype = (player: Player | null) => {
  if (!player) {
    return {
      archetype: "Unknown",
      radarData: [],
      hasDeepStats: false,
    };
  }

  const stats = player.advancedStats;
  const hasDeepStats = stats.totalPointsPlayed > 0;

  if (!hasDeepStats) {
    return {
      archetype: "Rookie",
      radarData: [],
      hasDeepStats: false,
    };
  }

  const servicePct = stats.servicePointsTotal > 0 ? (stats.servicePointsWon / stats.servicePointsTotal) * 100 : 0;
  const returnPct = stats.returnPointsTotal > 0 ? (stats.returnPointsWon / stats.returnPointsTotal) * 100 : 0;
  const conversionPct = stats.gamePointOpportunities > 0 ? (stats.gamePointsConverted / stats.gamePointOpportunities) * 100 : 0;
  const aggressionRate = stats.totalPointsPlayed > 0 ? ((stats.winners + stats.aces) / stats.totalPointsPlayed) * 100 : 0;
  const errorRate = stats.totalPointsPlayed > 0 ? (stats.errors / stats.totalPointsPlayed) * 100 : 0;
  const consistencyScore = Math.max(0, 100 - (errorRate * 2)); 

  let archetype = "Balanced";
  if (aggressionRate > 15 && errorRate > 15) archetype = "Glass Cannon";
  else if (returnPct > 55 && aggressionRate < 10) archetype = "The Wall";
  else if (aggressionRate > 15 && errorRate < 10) archetype = "The Sniper";
  else if (conversionPct > 75) archetype = "Clutch God";
  else if (servicePct > 60) archetype = "Ace Machine";

  const radarData = [
    { label: 'Serve', value: Math.min(100, Math.max(20, servicePct + 20)), tooltip: "Service Rating" }, 
    { label: 'Return', value: Math.min(100, Math.max(20, returnPct + 20)), tooltip: "Return Rating" },
    { label: 'Clutch', value: Math.min(100, Math.max(20, conversionPct)), tooltip: "Clutch Factor" },
    { label: 'Attack', value: Math.min(100, Math.max(20, aggressionRate * 3)), tooltip: "Aggression" }, 
    { label: 'Focus', value: Math.min(100, Math.max(20, consistencyScore)), tooltip: "Consistency" },
  ];

  return {
    archetype,
    radarData,
    hasDeepStats,
  };
};
